import React, { useState, useEffect } from "react";
import { Card } from "react-bootstrap";

const BankConsent = (props) => {
    const applId = props.urlparams.params.id;
    const [mobileNumber, setMobileNumber] = useState("");
    const [consentAccepted, setConsentAccepted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(1); // 1: Input, 2: Polling/Result
    const [consentData, setConsentData] = useState(null);
    const [periodicData, setPeriodicData] = useState(null);
    const [isPolling, setIsPolling] = useState(false);

    useEffect(() => {
        // Pre-fill mobile number from existing data if available
        if (props.editdata && props.editdata.mobileNumber) {
            setMobileNumber(props.editdata.mobileNumber);
        }
    }, [props.editdata]);

    const handleMobileChange = (e) => {
        setMobileNumber(e.target.value);
    };

    const handleConsentChange = (e) => {
        setConsentAccepted(e.target.checked);
    };

    const pollIntervalRef = React.useRef(null);
    const popupRef = React.useRef(null);

    const isMounted = React.useRef(true);

    useEffect(() => {
        return () => {
            isMounted.current = false;
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
            }
        };
    }, []);

    const initiateConsent = async () => {
        if (!mobileNumber) {
            swal2.fire({
                title: "Please enter a mobile number.",
                icon: "error",
                confirmButtonText: "OK",
            });
            return;
        }
        if (!consentAccepted) {
            swal2.fire({
                title: "Please accept the consent.",
                icon: "error",
                confirmButtonText: "OK",
            });
            return;
        }

        setLoading(true);

        // Step 1: initiateConsent -> use mobileNumber to call apiFinduitConsentUrl and get redirectUrl
        props.apis.apiFinduitConsentUrl({
            mobileNumber: mobileNumber,
        }, { applicationId: applId }).then((res) => {
            console.log("initiateConsent res", res);
            const redirectUrl = res?.redirectionUrl || res?.redirectionurl;
            if (redirectUrl) {
                // Open the URL in a new tab/window and store the reference
                popupRef.current = window.open(redirectUrl, "_blank", "width=800,height=600");
                setStep(2);
                // Step 2: startWindowMonitoring -> external finduit flow will happen on another window -> once external window closed do checkConsentStatus
                startWindowMonitoring();
            } else {
                swal2.fire({
                    title: "Failed to get redirection URL.",
                    icon: "error",
                    confirmButtonText: "OK",
                });
                setLoading(false);
            }
        }).catch((error) => {
            console.error("Error initiating consent:", error);
            swal2.fire({
                title: "An error occurred.",
                icon: "error",
                confirmButtonText: "OK",
            });
            setLoading(false);
        });
    };

    const startWindowMonitoring = () => {
        setIsPolling(true);

        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
        }

        pollIntervalRef.current = setInterval(() => {
            if (popupRef.current && popupRef.current.closed) {
                // Window has been closed
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
                console.log("Consent window closed. Checking for completion...");
                checkConsentStatus();
            }
        }, 1000);
    };

    const checkConsentStatus = () => {
        // Step 3: use applId to call apiGetCustomerSingleApplication -> response will contains finduitLog.webhookLogs (array of objs) -> do combination match on each obj using flags -> frequency : "1 times a DAY",fetchType : "ONETIME" -> get consentId from matched obj
        props.apis.apiGetCustomerSingleApplication({
            applicationId: applId,
        }).then((res) => {
            console.log("check status res", res);
            if (res) {
                const finduitLog = res?.finduitLogs;
                let consentIdFound = null;

                if (finduitLog?.webhookLogs && finduitLog.webhookLogs.length > 0) {
                    const logs = finduitLog.webhookLogs.map(logStr => {
                        try {
                            return typeof logStr === 'string' ? JSON.parse(logStr) : logStr;
                        } catch (e) {
                            console.error("Error parsing webhook log", e);
                            return null;
                        }
                    }).filter(Boolean);

                    const matchedLog = logs.find(log => log.frequency === "1 times a DAY" && log.fetchType === "ONETIME");
                    if (matchedLog && matchedLog.consentId) {
                        consentIdFound = matchedLog.consentId;
                    }
                }

                if (consentIdFound) {
                    console.log("consentId found:", consentIdFound);
                    fetchConsentData(consentIdFound);
                } else {
                    handleConsentFailure();
                }
            } else {
                handleConsentFailure();
            }
        }).catch((error) => {
            console.error("Error checking consent logs:", error);
            handleConsentFailure();
        });
    };

    const handleConsentFailure = () => {
        setIsPolling(false);
        setLoading(false);
        swal2.fire({
            title: "Consent process incomplete or failed.",
            text: "Please try again.",
            icon: "error",
            confirmButtonText: "OK",
        });
        setStep(1);
    };

    const fetchConsentData = async (consentId) => {
        setLoading(true);
        setIsPolling(true); // Ensure polling UI shows
        const delay = 3000;

        while (isMounted.current) {
            try {
                // Step 4: do apiFinduitConsentData using founded consentId -> get name and pdfbase64 -> convert base64toPDF
                // Pass header to suppress global error popups
                const res = await props.apis.apiFinduitConsentData({ consentId }, { "X-Suppress-Error": true });
                console.log("consent data res", res);

                if (res && res.data && res.data.length > 0) {
                    let pdfbase64 = res.data[0]?.dataDetail?.pdfbase64;
                    // Get name from nested structure as per existing project patterns
                    let name = res.data[0]?.dataDetail?.jsonData?.Account?.Profile?.Holders?.Holder?.name;

                    if (pdfbase64 && name) {
                        let pdfUrl = base64toPDF(pdfbase64, "bankstatement.pdf");
                        let data = {
                            pdf: pdfUrl,
                            name: name,
                        }
                        if (isMounted.current) {
                            setConsentData(data);
                            props.setConsentData(data); // Notify parent component
                            setIsPolling(false);
                            setLoading(false);
                        }
                        return; // Success, exit loop
                    }
                }
            } catch (error) {
                // Suppress error messages during polling as requested
                // console.error("Error fetching consent data, retrying...", error); 
            }

            // Wait before next attempt if still mounted
            if (isMounted.current) {
                await new Promise(resolve => setTimeout(resolve, delay));
            }
        }
    }

    const base64toPDF = (base64String, fileName = 'bankstatement.pdf') => {
        const base64Data = base64String.replace(/^data:application\/pdf;base64,/, "");
        const binaryString = window.atob(base64Data);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);

        for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }

        const blob = new Blob([bytes.buffer], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(blob);
        return blobUrl;
    }


    return (
        <div className="container-fluid py-0">
            <Card className="form_group_card row p-4">
                <h2 className="form_title black mb-3">Consent</h2>

                {step === 1 && (
                    <>
                        <div className="form-group mb-3">
                            <label className="form-label">Mobile No.</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Enter mobile no."
                                value={mobileNumber}
                                onChange={handleMobileChange}
                            />
                        </div>

                        <div className="form-check mb-4">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="consentCheck"
                                checked={consentAccepted}
                                onChange={handleConsentChange}
                            />
                            <label className="form-check-label" htmlFor="consentCheck">
                                I hereby authorize and give my consent to download and access my
                                bank statement soft copy from the bank's online portal for
                                verification and processing purposes.
                            </label>
                        </div>

                        <div className="d-flex justify-content-end">
                            <button className="btn btn-primary-lite me-2" onClick={(e) => props.changeTab(e, "bsa")}>
                                Skip For Now
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={initiateConsent}
                                disabled={loading}
                            >
                                {loading ? "Processing..." : "Proceed"}
                            </button>
                        </div>
                    </>
                )}

                {step === 2 && (
                    <>
                        {isPolling && !consentData && (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3">
                                    {loading ? "Fetching consent data, please wait..." : "Waiting for you to complete the process in the new window..."}
                                </p>
                            </div>
                        )}

                        {consentData && (
                            <div className="row">
                                <div className="col-md-4 mb-3">
                                    <label className="form-label text-muted">Mobile No.</label>
                                    <div className="form-control bg-light">{mobileNumber}</div>
                                </div>
                                <div className="col-md-4 mb-3">
                                    <label className="form-label text-muted">Name</label>
                                    <div className="form-control bg-light">{consentData.name || "N/A"}</div>
                                </div>
                                <div className="col-md-4 mb-3">
                                    <label className="form-label text-muted">Bank Statement</label>
                                    <div className="input-group">
                                        <input type="text" className="form-control" value="bankstatement.pdf" readOnly />
                                        <a href={consentData.pdf || "#"} target="_blank" rel="noopener noreferrer" className="btn btn-outline-secondary">
                                            <i className="fa fa-download"></i>
                                        </a>
                                    </div>
                                </div>

                                <div className="col-12 mt-3">
                                    <div className="form-check">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={true}
                                            readOnly
                                        />
                                        <label className="form-check-label text-muted">
                                            I hereby authorize and give my consent to download and access my
                                            bank statement soft copy from the bank's online portal for
                                            verification and processing purposes.
                                        </label>
                                    </div>
                                </div>

                                <div className="d-flex justify-content-end mt-4">
                                    <button className="btn btn-primary-lite me-2" onClick={(e) => props.changeTab(e, "bsa")}>
                                        Skip For Now
                                    </button>
                                    <button className="btn btn-primary" onClick={(e) => props.changeTab(e, "bsa")}>
                                        Save & Next
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </Card>
        </div>
    );
};

export default constore(BankConsent);
